import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { fetchMyProfile, updateMyProfile } from '../api/auth';
import { reportError } from '../utils/notify';
import { needsDisplayNameSetup } from '../utils/displayName';
import { useCompanyStore } from '../store/companyStore';
import './LoginPage.css';
import './SetupProfilePage.css';

function SetupProfilePage() {
  const navigate = useNavigate();
  const hasCompany = useCompanyStore((s) => s.hasCompany);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [alreadyOk, setAlreadyOk] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const profile = await fetchMyProfile();
        if (cancelled) return;
        if (!needsDisplayNameSetup(profile.nickname)) {
          setAlreadyOk(true);
          return;
        }
        const current = (profile.nickname || '').trim();
        if (current && needsDisplayNameSetup(current)) {
          setNickname('');
        } else {
          setNickname(current);
        }
      } catch {
        if (!cancelled) {
          reportError(setError, '프로필을 불러오지 못했습니다. 다시 로그인해 주세요.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const name = nickname.trim();
    if (needsDisplayNameSetup(name)) {
      reportError(setError, '이름은 2자 이상의 실명(또는 업무용 이름)으로 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await updateMyProfile({ nickname: name });
      window.dispatchEvent(new CustomEvent('linelog:profile-name', { detail: name }));
      navigate(hasCompany ? '/app' : '/companies', { replace: true });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '이름 저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  if (alreadyOk) {
    return <Navigate to={hasCompany ? '/app' : '/companies'} replace />;
  }

  return (
    <div className="login-page">
      <div className="login-page__panel setup-profile-page__panel">
        <div className="login-page__brand">
          <Link to="/" className="login-page__logo">
            <em>Line</em>log
          </Link>
          <p className="login-page__subtitle">회사 가입 전에 표시 이름을 정해 주세요</p>
        </div>

        {loading ? (
          <p className="setup-profile-page__hint">확인 중...</p>
        ) : (
          <form className="login-page__form" onSubmit={handleSubmit}>
            <p className="setup-profile-page__lead">
              동료·관리자가 누구인지 알아볼 수 있도록 <strong>실명 또는 업무용 이름</strong>을
              입력합니다. (카카오 닉네임·임시 이름은 사용할 수 없습니다)
            </p>

            {error && <p className="login-page__error">{error}</p>}

            <label>
              이름
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="예: 홍길동"
                maxLength={50}
                autoFocus
                autoComplete="name"
                disabled={saving}
              />
            </label>

            <button type="submit" className="login-page__submit" disabled={saving}>
              {saving ? '저장 중...' : '저장하고 계속'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default SetupProfilePage;
